import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronsUpDown, Search } from 'lucide-react';

export interface SearchSelectOption {
  readonly value: string;
  readonly label: string;
  /** Secondary text shown under the label and included in the filter. */
  readonly hint?: string;
}

interface SearchSelectProps {
  readonly options: readonly SearchSelectOption[];
  /** Controlled value. Omit and pass `defaultValue` for uncontrolled <form> usage. */
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  readonly defaultValue?: string;
  /**
   * Fires (with the raw filter text) whenever the user types in the filter box. Opt-in: use it when the
   * option set is too large to pre-load and must be searched server-side. The parent re-queries and
   * feeds fresh `options`; the built-in client filter then still narrows within them. Omit for the
   * normal fully-client case.
   */
  readonly onSearch?: (query: string) => void;
  readonly placeholder?: string;
  /** Renders a hidden input of this name mirroring the value — for FormData in uncontrolled forms. */
  readonly name?: string;
  readonly ariaLabel?: string;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
  readonly id?: string;
}

/**
 * A dropdown that is always a search-with-live-preview: a control button showing the selected
 * label, a popover with a filter input and the matching options. Works both controlled
 * (value/onChange) and uncontrolled in a <form> (defaultValue + name → hidden input). Scales from
 * a few options to hundreds — the FORQ-wide replacement for native <select>.
 */
export function SearchSelect({
  options,
  value,
  onChange,
  defaultValue,
  onSearch,
  placeholder = 'Auswählen …',
  name,
  ariaLabel,
  disabled,
  autoFocus,
  id,
}: SearchSelectProps) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue ?? '');
  const current = isControlled ? (value ?? '') : internal;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => options.find((option) => option.value === current) ?? null, [options, current]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('de');
    if (!needle) return options;
    return options.filter((option) => option.label.toLocaleLowerCase('de').includes(needle)
      || (option.hint ?? '').toLocaleLowerCase('de').includes(needle));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    function onDocMouseDown(event: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  function commit(next: string): void {
    if (!isControlled) setInternal(next);
    onChange?.(next);
  }

  function choose(option: SearchSelectOption): void {
    commit(option.value);
    setOpen(false);
    setQuery('');
  }

  function openList(): void {
    if (disabled) return;
    setQuery('');
    setActive(Math.max(0, options.findIndex((option) => option.value === current)));
    setOpen(true);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (disabled) return;
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openList(); }
      return;
    }
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((c) => Math.min(c + 1, filtered.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((c) => Math.max(c - 1, 0)); }
    else if (event.key === 'Enter') { event.preventDefault(); if (filtered[active]) choose(filtered[active]); }
    else if (event.key === 'Escape') { event.preventDefault(); setOpen(false); }
  }

  return (
    <div className={`forq-search-select${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="forq-search-select__value"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        autoFocus={autoFocus}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={handleKeyDown}
      >
        <span className={selected ? '' : 'is-placeholder'}>{selected ? selected.label : placeholder}</span>
        <ChevronsUpDown size={14} />
      </button>
      {open ? (
        <div className="forq-search-select__pop">
          <label className="forq-search-select__search">
            <Search size={14} />
            <input
              autoFocus
              type="text"
              value={query}
              placeholder="Tippen zum Filtern …"
              onChange={(event) => { setQuery(event.target.value); setActive(0); onSearch?.(event.target.value); }}
              onKeyDown={handleKeyDown}
            />
          </label>
          <ul className="forq-search-select__list" role="listbox">
            {filtered.map((option, index) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === current}
                  className={`${index === active ? 'is-active' : ''}${option.value === current ? ' is-selected' : ''}`}
                  onMouseDown={(event) => { event.preventDefault(); choose(option); }}
                  onMouseMove={() => setActive(index)}
                >
                  <span className="forq-search-select__label">{option.label}</span>
                  {option.hint ? <span className="forq-search-select__hint">{option.hint}</span> : null}
                </button>
              </li>
            ))}
            {filtered.length === 0 ? <li className="forq-search-select__empty">Kein Treffer</li> : null}
          </ul>
        </div>
      ) : null}
      {name ? <input type="hidden" name={name} value={current} readOnly /> : null}
    </div>
  );
}
